/**
 * /accounting/bank-rec — Bank & Credit Card reconciliation hub.
 *
 * Lists every mapped bank/CC account with: unmatched count, $ inflow/outflow
 * waiting to clear, last import time. Click → per-account match workspace.
 */
import { Link } from "react-router-dom";
import { Banknote, CreditCard, Upload } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useBankFeed } from "@/data/accounting/bankFeed/BankFeedContext";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";

import { BooksNavRail } from "@/components/accounting/BooksNavRail";
const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";

export default function BankReconciliationHubPage() {
  const { accounts, getSummary } = useBankFeed();
  const { accountById } = useGeneralLedger();

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 data-tour-id="bank-rec-accounts" className="text-2xl font-semibold tracking-tight">Bank & Credit Card Reconciliation</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Match bank/CC statement lines to the General Ledger. Cleared deposits release Unreconciled Cash to real cash.
          </p>
        </div>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {accounts.map(a => {
            const acc = accountById.get(a.glAccountId);
            const s = getSummary(a.glAccountId);
            const Icon = a.kind === "bank" ? Banknote : CreditCard;
            const unmatched = s?.unmatchedCount ?? 0;
            return (
              <Link
                key={a.glAccountId}
                to={`/accounting/bank-rec/${a.glAccountId}`}
                className="rounded-md border border-border bg-card p-4 hover:bg-accent/40 transition-colors flex items-start gap-3"
              >
                <div className="h-9 w-9 rounded-sm bg-muted flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium truncate">{a.label}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {acc?.number}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {acc?.name ?? "Unmapped GL account"}
                  </p>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-muted-foreground">Unmatched</div>
                      <div className={`font-semibold tabular-nums ${unmatched > 0 ? "text-destructive" : ""}`}>
                        {unmatched}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Inflow</div>
                      <div className="font-semibold tabular-nums">{fmt(s?.unmatchedInflow ?? 0)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Outflow</div>
                      <div className="font-semibold tabular-nums">{fmt(s?.unmatchedOutflow ?? 0)}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Last import: {fmtDate(s?.lastImportAt)}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="rounded-md border border-border bg-card p-4 text-sm">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">How import works</span>
          </div>
          <p className="text-muted-foreground">
            From any account workspace you can paste a CSV with columns{" "}
            <span className="font-mono text-foreground">date, description, amount, reference</span>.
            Positive amounts are inflows, negatives are outflows. Plaid will plug in here later — same engine, no UI change.
          </p>
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}
